import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Stat, EvidenceBadge } from "@/components/shared";
import { api, errMsg, salaryText, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HiringFeed } from "@/components/HiringFeed";
import CandidateHired from "@/components/CandidateHired";

function ContactRequests() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/candidate/contact-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const respond = async (id, accept) => {
    try { await api.post(`/candidate/contact-requests/${id}`, { accept }); toast.success(accept ? "Contact shared with recruiter" : "Request declined"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows.length) return <p className="text-sm text-muted-foreground">No recruiter requests. Turn on visibility in the Privacy center to be discoverable.</p>;
  return rows.map((r) => (
    <div key={r.request_id} className="border-b py-3 last:border-0" data-testid={`contact-request-${r.request_id}`}>
      <p className="text-sm font-medium">{r.recruiter_name} · {r.org_name}</p>
      <p className="mt-1 text-sm text-muted-foreground">{r.message}</p>
      {r.state === "pending" ? (
        <div className="mt-2 flex gap-2"><Button size="sm" onClick={() => respond(r.request_id, true)} data-testid="contact-accept-button">Share my email</Button>
          <Button size="sm" variant="outline" onClick={() => respond(r.request_id, false)} data-testid="contact-decline-button">Decline</Button></div>
      ) : <p className="mt-1 text-xs capitalize text-muted-foreground">{r.state}</p>}
    </div>
  ));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState({ quota: null, apps: [], recs: [], facets: null, profile: null });
  useEffect(() => {
    Promise.all([api.get("/ai/quota"), api.get("/applications"), api.get("/autopilot"), api.get("/jobs/facets"), api.get("/profile")])
      .then(([q, a, ap, f, p]) => setD({ quota: q.data, apps: a.data, recs: ap.data.recommendations, facets: f.data, profile: p.data }));
  }, []);
  const p = d.profile || {};
  const complete = ["headline", "seniority", "skills", "target_titles", "min_salary", "resume_text"].filter((k) => (Array.isArray(p[k]) ? p[k].length : p[k])).length;
  return (
    <div>
      <PageHeader eyebrow="Candidate dashboard" title={`Good to see you, ${user.name?.split(" ")[0] || "there"}`}>
        <Button asChild className="rounded-full" data-testid="dashboard-discover-button"><Link to="/app/jobs">Discover roles</Link></Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-inventory" labelText="Verified openings" value={d.facets?.total?.toLocaleString() ?? "—"} hint={`${d.facets?.salary_disclosed?.toLocaleString() ?? "—"} with disclosed pay`} />
        <Stat testid="stat-applications" labelText="Applications" value={d.apps.length} hint={`${d.apps.filter((a) => a.evidence === "confirmed").length} confirmed`} />
        <Stat testid="stat-credits" labelText="AI credits used" value={d.quota ? `${d.quota.credits_used}/${d.quota.credits_limit}` : "—"} hint={d.quota?.plan_name} />
        <Stat testid="stat-profile" labelText="Profile strength" value={`${Math.round((complete / 6) * 100)}%`} hint={complete < 6 ? "Complete it for better matching" : "Ready for matching"} />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12 [&>*]:min-w-0">
        <HiringFeed />
        <Panel className="lg:col-span-7" testid="dashboard-recommendations">
          <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-semibold">Autopilot suggestions</h2><Link to="/app/autopilot" className="text-sm text-primary">Manage</Link></div>
          {d.recs.length === 0 ? <p className="text-sm text-muted-foreground">No suggestions yet. Complete your profile and run Career Autopilot.</p> :
            d.recs.slice(0, 5).map((r) => (
              <Link key={r.rec_id} to={`/app/jobs/${r.job_id}`} className="flex items-center justify-between border-b py-3 last:border-0 hover:text-primary">
                <div><p className="font-medium">{r.job_snapshot.title}</p><p className="text-xs text-muted-foreground">{r.job_snapshot.employer} · {salaryText(r.job_snapshot)}</p></div>
                <span className="font-mono text-sm">{r.score} <ArrowUpRight className="inline h-3.5 w-3.5" /></span>
              </Link>
            ))}
        </Panel>
        <Panel className="lg:col-span-5" testid="dashboard-contact-requests"><h2 className="mb-4 text-lg font-semibold">Recruiter requests</h2><ContactRequests /><CandidateHired /></Panel>
        <Panel className="lg:col-span-12" testid="dashboard-recent-applications">
          <h2 className="mb-4 text-lg font-semibold">Recent applications</h2>
          {d.apps.length === 0 ? <p className="text-sm text-muted-foreground">Nothing yet.</p> : d.apps.slice(0, 6).map((a) => (
            <div key={a.application_id} className="flex flex-col gap-2 border-b py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
              <div><p className="font-medium">{a.job_snapshot.title}</p><p className="text-xs text-muted-foreground">{a.job_snapshot.employer} · {label(a.mode)} · {label(a.status)}</p></div>
              <EvidenceBadge value={a.evidence} />
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}
